import type { MantineTheme } from '@mantine/core';
import { Group, Stack, Textarea } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { Button, Select, Text, TextInput } from '@repo/foundations';
import ed25519 from 'ed25519-keygen/ssh';
import { randomBytes } from 'ed25519-keygen/utils';
import { Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import type { ITunnel } from '../../../api';
import { queryClient, tunnelsQueryKeyFactory } from '../../../api';
import { Whitelist } from '../../../components/Integration/IntegrationDisclaimers';
import { closeAllModals, openModal } from '../../../components/ModalManager';
import { generateKeyPair } from '../../TunnelPage/utils';

// Validation regex constants
const validIpAddressRegex =
	/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/i;
const validHostnameRegex =
	/^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/i;
const loopbackRegex =
	/^localhost$|^127(?:\.[0-9]+){0,2}\.[0-9]+$|^(?:0*\:)*?:?0*1$/i;

interface TunnelFormValues {
	username: string;
	host: string;
	port: number;
	type: 'ed25519' | 'RSA';
}

interface TunnelFormErrors {
	username?: string;
	host?: string;
	port?: string;
	type?: string;
}

interface TunnelCreationData extends Partial<ITunnel> {
	private_key?: string;
}

interface CreateForwardTunnelModalProps {
	onCreateTunnel: (params: { data: Partial<ITunnel> }) => Promise<ITunnel>;
	theme: MantineTheme;
}

function validateForm(values: TunnelFormValues): TunnelFormErrors {
	const errors: TunnelFormErrors = {};

	if (!values.username) {
		errors.username = 'Fill this field';
	} else if (!values.username.match(/^[a-z_][a-z0-9_-]*[$]?$/i)) {
		errors.username = 'Enter a valid username';
	}

	if (!values.host) {
		errors.host = 'Fill this field';
	} else {
		if (values.host.match(loopbackRegex)) {
			errors.host = 'Loopback addresses are not allowed.';
		} else if (
			!values.host.match(validIpAddressRegex) &&
			!values.host.match(validHostnameRegex)
		) {
			errors.host = 'Enter a valid ip address or hostname.';
		}
	}

	if (!values.port) {
		errors.port = 'Fill this field';
	} else if (!parseInt(values.port.toString(), 10)) {
		errors.port = 'Enter a valid port';
	}

	return errors;
}

function CreateForwardTunnelModal({
	onCreateTunnel,
	theme,
}: CreateForwardTunnelModalProps) {
	const clipboard = useClipboard();
	type LoadingState = 'idle' | 'generating-keys' | 'creating-tunnel';
	const [loadingState, setLoadingState] = useState<LoadingState>('idle');

	const handleSubmit = useCallback(
		async (values: TunnelFormValues) => {
			try {
				setLoadingState('generating-keys');
				let input: TunnelCreationData = { ...values };
				let copyText: string | null = null;

				// Generate key pair based on selected algorithm
				if (values.type === 'RSA') {
					const rsaKeys = await generateKeyPair({
						alg: 'RSASSA-PKCS1-v1_5',
						size: 4096,
						hash: 'SHA-512',
						name: 'ssh@secoda.co',
					});
					input.private_key = btoa(rsaKeys.privateKey);
					copyText = rsaKeys.publicKey;
				} else {
					const ed25519Keys = await ed25519(randomBytes(32), 'ssh@secoda.co');
					input.private_key = btoa(ed25519Keys.privateKey);
					copyText = ed25519Keys.publicKey;
				}

				setLoadingState('creating-tunnel');

				// Create the tunnel
				await onCreateTunnel({ data: input });

				// Invalidate queries to refresh the tunnel list
				queryClient.invalidateQueries({
					queryKey: tunnelsQueryKeyFactory.allLists(),
				});

				setLoadingState('idle');
				closeAllModals();

				// Show the public key in a new modal
				openModal({
					variant: 'default',
					title: 'Forward tunnel created',
					children: (
						<Stack spacing={theme.spacing.md} mt={0}>
							<Stack spacing={theme.spacing.xs}>
								<Text>
									This public key will only be displayed once. Please copy it to
									your clipboard and add it to the SSH bastion&apos;s{' '}
									<code>authorized_keys</code> file
								</Text>

								<Textarea value={copyText} minRows={6} readOnly />
							</Stack>
							<Group
								sx={{
									justifyContent: 'flex-end',
								}}
							>
								<Button
									onClick={() => {
										clipboard.copy(copyText);
										showNotification({
											title: 'Public key copied',
											color: 'green',
											message:
												'The public key has been copied to your clipboard',
										});
									}}
								>
									Copy public key
								</Button>
							</Group>
						</Stack>
					),
				});
			} catch (error) {
				setLoadingState('idle');

				showNotification({
					title: 'Error creating tunnel',
					color: 'red',
					message: 'Failed to create tunnel. Please try again.',
				});
			}
		},
		[onCreateTunnel, theme.spacing.md, theme.spacing.xs, clipboard]
	);

	const isSubmitting = loadingState !== 'idle';

	const getButtonText = (state: LoadingState): string => {
		switch (state) {
			case 'generating-keys':
				return 'Generating keys...';
			case 'creating-tunnel':
				return 'Creating tunnel...';
			default:
				return 'Create tunnel';
		}
	};

	return (
		<Stack spacing={theme.spacing.md} mt={theme.spacing.md}>
			<Formik<TunnelFormValues>
				initialValues={{
					username: '',
					host: '',
					port: 22,
					type: 'ed25519',
				}}
				validate={validateForm}
				onSubmit={handleSubmit}
			>
				{({
					handleSubmit,
					errors,
					touched,
					handleChange,
					handleBlur,
					values,
				}) => {
					const getFieldError = (
						field: keyof TunnelFormErrors
					): string | undefined =>
						touched[field] && errors[field] ? errors[field] : undefined;

					return (
						<Form onSubmit={handleSubmit}>
							<Stack mt={0} spacing={theme.spacing.md}>
								<TextInput
									data-autofocus
									label="SSH Username"
									description="Ensure that this SSH user exists on the bastion"
									error={getFieldError('username')}
									name="username"
									value={values.username}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="secoda"
									disabled={isSubmitting}
								/>

								<TextInput
									label="SSH Hostname"
									description="The IP or public hostname of the bastion"
									error={getFieldError('host')}
									name="host"
									value={values.host}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder=""
									disabled={isSubmitting}
								/>

								<TextInput
									label="SSH Port"
									description="The port the bastion is listening on. The default SSH port is 22"
									error={getFieldError('port')}
									name="port"
									value={values.port}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="22"
									disabled={isSubmitting}
								/>

								<Select
									label="Pick the SSH algorithm"
									description="ed25519 is more secure and compatible with modern systems. RSA is more compatible with older systems"
									data={[
										{ value: 'ed25519', label: 'ed25519' },
										{ value: 'RSA', label: 'RSA' },
									]}
									value={values.type}
									onChange={(value) => {
										handleChange({
											target: { name: 'type', value },
										});
									}}
									disabled={isSubmitting}
								/>

								<Whitelist />

								<Group
									spacing={theme.spacing.sm}
									sx={{
										justifyContent: 'flex-end',
									}}
								>
									<Button
										variant="tertiary"
										onClick={() => closeAllModals()}
										disabled={isSubmitting}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										loading={isSubmitting}
										disabled={isSubmitting}
									>
										{getButtonText(loadingState)}
									</Button>
								</Group>
							</Stack>
						</Form>
					);
				}}
			</Formik>
		</Stack>
	);
}

export default CreateForwardTunnelModal;
